import React, {useState} from "react";
import HomeSideNavBar from "../HomeSideNavBar";
import Header from "../Header";
import ApiDataProvider from "../ApiDataContext";
import {PieChart, FilePen, ListChecks} from "lucide-react";
import CreateTask from "./CreateTask";
import TaskList from "./TaskList";
import TaskReport from "./TaskReport";
import * as Constants from "../../utils/Constants";
import {getAuthToken} from '../../utils/utility';
import {hasPermission} from "../../utils/rolePermissions";
import "../../styles/components/tasks.css";
import PageHeader from "../../components/layout/PageHeader";
import Tabs from "../../components/ui/Tabs";
import BackToDashboardButton from "../../components/ui/BackToDashboardButton";

function Tasks() {
    const TASK_LIST   = 'TaskList';
    const CREATE_TASK = 'CreateTask';
    const TASK_REPORT = 'TaskReport';

    const TAB_META = {
        [TASK_LIST]:   { label: "Task List",          subtitle: "View, search, and manage all tasks." },
        [CREATE_TASK]: { label: "Create / Edit Task", subtitle: "Build a new task or update an existing one." },
        [TASK_REPORT]: { label: "Task Reports",       subtitle: "Status summary across recent task activity." },
    };

    const canManageTasks = hasPermission("tasks_manage");

    const [selectedComponent, setSelectedComponent] = useState(TASK_LIST);
    const [taskToCopy, setTaskToCopy] = useState(null);
    const activeMeta = TAB_META[selectedComponent] || { label: "Tasks", subtitle: "" };

    const handleCopyTask = (task) => {
        fetchCustomers(task);
    };

    const fetchCustomers = (task) => {
        const endPoint = `${Constants.BASE_URL}${Constants.API_V1}/tasks/${task.taskId}/customers`;
    
        fetch(endPoint, {
            method: 'GET',
            headers: { 'Authorization': `${Constants.BEARER} ${getAuthToken()}` }
        })
        .then(response => response.json())
        .then(data => {
            const updatedCustomerList = data.map(customer => {
                const { taskLocationId, ...rest } = customer;
                return rest;
            });
            const updatedTask = { ...task, customerList: updatedCustomerList };
            setTaskToCopy(updatedTask);
            setTimeout(() => { setSelectedComponent(CREATE_TASK); }, 100);
        })
        .catch(error => console.error('Error fetching customers:', error));
    };

    const renderSelectedComponent = () => {
        switch (selectedComponent) {
            case TASK_LIST:
                return <TaskList onCopyTask={handleCopyTask} />;
            case CREATE_TASK:
                return canManageTasks
                    ? <CreateTask taskToCopy={taskToCopy} setTaskToCopy={setTaskToCopy} />
                    : <TaskList onCopyTask={handleCopyTask} />;
            case TASK_REPORT:
                return canManageTasks ? <TaskReport /> : <TaskList onCopyTask={handleCopyTask} />;
            default:
                return null;
        }
    };

    const tabItems = [
        {value: TASK_LIST,   label: "Task List",          icon: ListChecks},
        canManageTasks && {value: CREATE_TASK, label: "Create / Edit", icon: FilePen},
        canManageTasks && {value: TASK_REPORT, label: "Reports",       icon: PieChart},
    ].filter(Boolean);

    return (
        <ApiDataProvider>
            <div className="container">
                <div className="screen-layout">
                    <Header />
                    <HomeSideNavBar />
                    <main className="flex-1 min-w-0 min-h-0 flex flex-col bg-surface-muted overflow-y-auto">
                        <PageHeader
                            title="Tasks"
                            subtitle={activeMeta.subtitle}
                            breadcrumbs={[{label: "Tasks"}, {label: activeMeta.label}]}
                            actions={<BackToDashboardButton />}
                        />
                        <Tabs value={selectedComponent} onChange={setSelectedComponent} items={tabItems} className="bg-white" />
                        <div className="flex-1 min-h-0 px-2 sm:px-4 lg:px-6 py-4">
                            {renderSelectedComponent()}
                        </div>
                    </main>
                </div>
            </div>
        </ApiDataProvider>
    );
}


export default Tasks;
